/**
 * GA4 전송 — **전 표면 공통**(브라우저·Android·iOS). gtag.js 를 쓰지 않고 `/g/collect` 로 직접 보낸다.
 *
 * # 왜 직접 보내나
 * 출발점은 iOS 였다. iOS WebView 의 origin 은 `app://app.heviton.local` 인데 **gtag.js 는 비 HTTP
 * origin 에서 전송을 하지 않는다.** 실측(시뮬레이터 + Safari 웹 인스펙터): 스크립트 로드 ✓, 컨테이너
 * 초기화(`google_tag_manager['G-…']`) ✓, 에러 없음 ✓ 인데 Resource Timing 에 `/g/collect` 가 **0건**.
 * 같은 URL 을 `fetch` 로 직접 치면 **204 + DebugView 기록**까지 정상이다. 즉 막는 주체는 네트워크도
 * CSP 도 아니고 gtag.js 자신이라 클라이언트 설정으로는 우회할 수 없다.
 *
 * iOS 만 직접 전송으로 두면 **표면마다 세션·이벤트 정의가 갈린다**. 웹 1명 = 앱 1명이라는 전제가
 * 깨지면 웹↔앱 비교 자체가 무의미해지므로, **전 표면을 같은 경로로 통일**한다.
 *
 * # 덤으로 얻는 것
 *   - 서드파티 스크립트 0 — 초기 로드가 가벼워지고, 애드블록의 스크립트 차단에 영향받지 않는다.
 *   - `api_secret` 불필요 — `/g/collect` 는 gtag 가 쓰는 그 엔드포인트다(`/mp/collect` 와 다르다).
 *   - iOS 도 30분 무활동 세션을 갖는다(쿠키는 버려지지만 **localStorage 는 정상 동작**한다).
 *
 * # 대신 우리가 책임지는 것 (gtag 가 해주던 것)
 *   - 세션(`sid`·`sct`·`seg`) · 참여 시간(`_et`) · 첫 방문/세션 시작 플래그(`_fv`·`_ss`)
 *   - 유입 경로 — `dr`(referrer) 와 `dl` 의 캠페인 파라미터를 실어 **GA4 가 서버에서 파싱**하게 한다.
 *   - 화면 이탈 시 `user_engagement` — 없으면 "한 화면만 오래 보고 나간" 체류가 통째로 누락된다.
 *
 * # 알려진 손실
 *   - **향상된 측정**(scroll·외부링크 click·file_download·form_*)은 발생하지 않는다. 필요해지면
 *     해당 이벤트를 직접 만들어 보낸다(그러면 세 표면 모두 동일하게 동작한다).
 *   - User-Agent Client Hints(`uafvl` 등)를 보내지 않아 브라우저·기기 세부 해상도가 UA 문자열
 *     수준으로 낮아진다. 브라우저·OS 판별에는 지장이 없다.
 */

const ENDPOINT = 'https://www.google-analytics.com/g/collect';
const SESSION_KEY = 'heviton.ga_session';
/** GA4 표준 세션 만료 — 30분 무활동. */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
/** GA4 "참여 세션" 판정 — 10초 이상 머물거나 히트 2건 이상. */
const ENGAGED_MS = 10_000;
const ENGAGED_HITS = 2;

/**
 * `dl` 에 실어 보낼 **캠페인 파라미터 화이트리스트**. GA4 는 `dl` 의 쿼리스트링을 서버에서 파싱해
 * 소스/매체를 판정하므로 이 값들만 살려 보낸다.
 *
 * ⚠️ 화이트리스트인 것이 핵심이다 — 쿼리스트링을 통째로 실으면 필터 상태로 URL 카디널리티가 폭발하고,
 * 무엇보다 **웹뷰 진입 토큰(`t`)** 같은 민감한 값이 제3자에게 새어 나간다.
 */
const CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
];

/**
 * GA4 canonical 파라미터명 → wire 파라미터명. 이 이름들은 `ep.*` 커스텀 파라미터가 아니라 **전용
 * 필드**라, 잘못 보내면 GA4 가 페이지·유입 같은 기본 측정기준을 못 채운다.
 */
const WIRE_NAME: Record<string, string> = {
  page_location: 'dl',
  page_title: 'dt',
  page_referrer: 'dr',
  traffic_type: 'tt',
};

/** gtag `config` 전용 지시자 — 이벤트 파라미터가 아니므로 wire 로 내보내지 않는다. */
const CONFIG_ONLY = new Set(['send_page_view', 'client_id', 'client_storage', 'debug_mode']);

export interface CollectConfig {
  measurementId: string;
  clientId: string;
  /** 모든 히트에 실리는 공통 파라미터. */
  params: Record<string, unknown>;
}

interface SessionState {
  /** GA4 `sid` — 세션 시작 시각(unix seconds). */
  id: number;
  /** GA4 `sct` — 이 사용자의 몇 번째 세션인가. */
  count: number;
  /** 마지막 히트 시각(ms) — 30분 무활동 판정 기준. */
  lastHitAt: number;
  /** 세션 시작 시각(ms) — 참여 세션(10초) 판정 기준. */
  startedAt: number;
  /** 이 세션에서 보낸 히트 수 — 참여 세션(2건) 판정 기준. */
  hits: number;
}

let config: CollectConfig | null = null;
/** `_p` — 페이지 로드 식별자. 로드마다 1회 생성. */
let pageLoadId = 0;
/** `_s` — 이 페이지 로드에서 보낸 히트 수(1부터). */
let hitCount = 0;
/** 직전 히트 시각 — 참여 시간(`_et`) 계산용. */
let previousHitAt = 0;
/** 아직 `dr`(referrer)을 안 보냈는가 — 유입 판정은 첫 히트에만 필요하다. */
let referrerPending = true;

/** 전송 활성화. 미호출 상태에서 [collectEvent] 는 no-op. */
export function startCollect(next: CollectConfig): void {
  config = next;
  pageLoadId = Math.floor(Math.random() * 2 ** 31);
  hitCount = 0;
  previousHitAt = 0;
  referrerPending = true;
  observeEngagement();
}

/**
 * 이벤트 1건 전송. 실패는 삼킨다 — 분석 하나로 앱이 흔들릴 이유가 없다(모듈 전체의 fail-open 원칙).
 */
export function collectEvent(name: string, eventParams?: Record<string, unknown>): void {
  if (!config) return;

  const now = Date.now();
  const { session, isNewSession, isFirstVisit } = touchSession(now);
  hitCount += 1;

  const query = new URLSearchParams({
    v: '2',
    tid: config.measurementId,
    cid: config.clientId,
    sid: String(session.id),
    sct: String(session.count),
    _p: String(pageLoadId),
    _s: String(hitCount),
    en: name,
    // 참여 시간(ms). GA4 는 이 누적으로 평균 참여 시간을 낸다. 첫 히트는 최소값 1.
    _et: String(previousHitAt ? Math.max(1, now - previousHitAt) : 1),
    // 참여 세션 여부. **항상 1 로 두면 참여율이 부풀려진다** — GA4 기준(10초 또는 2히트)을 그대로 쓴다.
    seg: now - session.startedAt >= ENGAGED_MS || session.hits >= ENGAGED_HITS ? '1' : '0',
  });

  // 세션·사용자 파생 신호 — GA4 는 이 플래그로 `session_start`·`first_visit` 을 **서버에서 만들어 낸다**.
  // 우리가 그 두 이벤트를 직접 보내는 게 아니라 플래그만 세우는 것이 정석이다.
  if (isNewSession) query.set('_ss', '1');
  if (isFirstVisit) query.set('_fv', '1');

  // 유입 경로 — 첫 히트에만. GA4 가 `dr` + `dl` 의 캠페인 파라미터로 소스/매체를 판정한다.
  if (referrerPending) {
    referrerPending = false;
    const referrer = safeReferrer();
    if (referrer) query.set('dr', referrer);
  }

  appendContext(query);
  appendParams(query, { ...config.params, ...eventParams });

  previousHitAt = now;

  try {
    // keepalive — 화면 전환·앱 백그라운드 전환 중에도 전송이 끊기지 않게.
    void fetch(`${ENDPOINT}?${query.toString()}`, { method: 'POST', keepalive: true }).catch(
      () => {},
    );
  } catch {
    // fetch 생성 자체가 실패해도 무시.
  }
}

/**
 * 화면이 가려질 때 `user_engagement` 를 보낸다 — gtag 가 해주던 일.
 *
 * 없으면 **한 화면만 오래 보고 나간 체류가 통째로 누락된다**. `_et` 는 다음 히트에 실려 나가는데,
 * 마지막 화면에는 다음 히트가 없기 때문이다. `visibilitychange` 는 앱 백그라운드 전환에서도 발화한다.
 */
function observeEngagement(): void {
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') collectEvent('user_engagement');
    });
  } catch {
    // 이벤트 등록 실패는 무시 — 참여 시간 정확도만 낮아진다.
  }
}

/** 화면·언어 등 gtag 가 자동으로 붙여 주던 기본 컨텍스트. */
function appendContext(query: URLSearchParams): void {
  try {
    query.set('ul', navigator.language.toLowerCase());
    query.set('sr', `${window.screen.width}x${window.screen.height}`);
    if (document.title) query.set('dt', document.title);
  } catch {
    // 접근 실패해도 전송은 계속한다 — 부가 정보일 뿐이다.
  }
}

function safeReferrer(): string {
  try {
    return document.referrer;
  } catch {
    return '';
  }
}

/**
 * 현재 URL 의 **캠페인 파라미터만** 골라 `dl` 뒤에 붙인다. 나머지 쿼리스트링은 버린다
 * (카디널리티 폭발 + 진입 토큰 유출 방지 — [CAMPAIGN_PARAMS] 주석 참조).
 */
function withCampaignParams(pageLocation: string): string {
  try {
    const current = new URLSearchParams(window.location.search);
    const kept = new URLSearchParams();
    for (const key of CAMPAIGN_PARAMS) {
      const value = current.get(key);
      if (value) kept.set(key, value);
    }
    const suffix = kept.toString();
    return suffix ? `${pageLocation}?${suffix}` : pageLocation;
  } catch {
    return pageLocation;
  }
}

/**
 * 파라미터를 wire 형식으로 변환.
 *   - 전용 필드(`page_location` 등)는 [WIRE_NAME] 으로 치환
 *   - `debug_mode` 는 값이 아니라 `_dbg=1` 플래그
 *   - 나머지는 커스텀 파라미터 — **숫자는 `epn.`, 문자열은 `ep.`** (GA4 가 타입을 그걸로 가른다)
 */
function appendParams(query: URLSearchParams, params: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (key === 'debug_mode') {
      if (value) query.set('_dbg', '1');
      continue;
    }
    if (CONFIG_ONLY.has(key)) continue;

    const wire = WIRE_NAME[key];
    if (wire === 'dl') {
      query.set('dl', withCampaignParams(String(value)));
    } else if (wire) {
      query.set(wire, String(value));
    } else if (typeof value === 'number') {
      query.set(`epn.${key}`, String(value));
    } else {
      query.set(`ep.${key}`, String(value));
    }
  }
}

/**
 * 세션 상태 갱신. 30분 무활동이면 새 세션을 열고 세션 카운터를 올린다.
 *
 * 저장 실패(차단 환경)는 삼키고 **매 히트 새 세션**으로 떨어진다 — 지표 정확도만 낮아지고 동작엔 무해하다.
 */
function touchSession(now: number): {
  session: SessionState;
  isNewSession: boolean;
  isFirstVisit: boolean;
} {
  const previous = readSession();
  const expired = !previous || now - previous.lastHitAt > SESSION_TIMEOUT_MS;

  const session: SessionState = expired
    ? {
        id: Math.floor(now / 1000),
        count: (previous?.count ?? 0) + 1,
        lastHitAt: now,
        startedAt: now,
        hits: 1,
      }
    : { ...previous, lastHitAt: now, hits: previous.hits + 1 };

  writeSession(session);
  return { session, isNewSession: expired, isFirstVisit: !previous };
}

function readSession(): SessionState | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as SessionState).id === 'number' &&
      typeof (parsed as SessionState).count === 'number' &&
      typeof (parsed as SessionState).lastHitAt === 'number'
    ) {
      const state = parsed as SessionState;
      // 구버전 저장분 보정 — 필드가 늘어나도 세션이 통째로 깨지지 않게 기본값을 채운다.
      return {
        ...state,
        startedAt: typeof state.startedAt === 'number' ? state.startedAt : state.lastHitAt,
        hits: typeof state.hits === 'number' ? state.hits : 1,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function writeSession(session: SessionState): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // 저장 차단 환경 — 세션이 매번 새로 열릴 뿐 동작엔 무해.
  }
}
